/**
 * S5 — Rep "My leads" list (`/leads`). The signed-in workspace sales rep's
 * "contact this lead" handoff tasks (NOT a reply inbox). Two tabs: Active (open)
 * and History (worked / escalated / closed). Each row shows the agent's handoff
 * context, the lead ref, a first-touch "Contact by" deadline (claret when
 * at-risk), and a status chip.
 *
 * Honest data posture: the list shows the lead person/company name (resolved
 * server-side from the refs) as the headline, with the agent's context summary as
 * the substance; when a name is missing it falls back to a short Martian-Mono ref
 * and never fabricates one. Empty → "Žádné leady k oslovení".
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S5.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type DataTableColumn,
} from '@teivor/shared-ui';
import { formatDateTime } from '@teivor/shared-utils';

import { PageContainer } from '../../../components/PageContainer';

import { useRepTasksQuery } from './queries';
import type { RepTask, RepTaskTab } from './types';
import { ACTIVE_STATUSES } from './types';
import { dueState, isAtRisk, statusBadge } from './shared';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

export function RepLeadsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState<RepTaskTab>('active');

  const { data, isLoading, isError, refetch } = useRepTasksQuery();

  const tasks = data ?? [];
  const active = React.useMemo(
    () => tasks.filter((task) => ACTIVE_STATUSES.includes(task.taskStatus)),
    [tasks],
  );
  const history = React.useMemo(
    () => tasks.filter((task) => !ACTIVE_STATUSES.includes(task.taskStatus)),
    [tasks],
  );
  const atRiskCount = React.useMemo(
    () => active.filter((task) => isAtRisk(task.firstTouchDueAt, task.taskStatus)).length,
    [active],
  );

  const columns = React.useMemo<DataTableColumn<RepTask>[]>(
    () => [
      {
        id: 'context',
        header: t('com.rep.col.context', 'Kontext / Context'),
        primary: true,
        minWidth: 240,
        width: 360,
        cell: (row) =>
          row.contextSummary?.trim() ? (
            <span className="block max-w-[44ch] truncate text-[var(--tvl-ink)]" title={row.contextSummary}>
              {row.contextSummary}
            </span>
          ) : (
            <span className="text-[var(--tvl-ink-3)]">{t('com.rep.noContext', 'Bez kontextu / No context')}</span>
          ),
      },
      {
        id: 'lead',
        header: t('com.rep.col.lead', 'Lead / Lead'),
        minWidth: 160,
        width: 200,
        cell: (row) =>
          row.personName || row.companyName ? (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-[13px] text-[var(--tvl-ink)]" title={row.personName ?? undefined}>
                {row.personName ?? row.companyName}
              </span>
              {row.personName && row.companyName ? (
                <span className="truncate text-[12px] text-[var(--tvl-ink-3)]" title={row.companyName}>
                  {row.companyName}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-[12px] text-[var(--tvl-ink-3)]" style={{ fontFamily: 'var(--tvl-font-mono)' }}>
              {row.leadPersonRef.slice(0, 8)}
            </span>
          ),
      },
      {
        id: 'due',
        header: t('com.rep.col.due', 'Kontaktovat do / Contact by'),
        width: 170,
        cell: (row) => {
          const state = dueState(row.firstTouchDueAt, row.taskStatus);
          const danger = state === 'overdue' || state === 'at_risk';
          return (
            <span
              className="tabular-nums"
              style={{ color: danger ? 'var(--tvl-accent)' : 'var(--tvl-ink-2)' }}
            >
              {formatDateTime(row.firstTouchDueAt, locale)}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: t('com.rep.col.status', 'Stav / Status'),
        width: 150,
        cell: (row) => {
          const spec = statusBadge(row.taskStatus);
          return <Badge variant={spec.variant}>{t(spec.key, spec.fallback)}</Badge>;
        },
      },
    ],
    [t, locale],
  );

  return (
    <PageContainer width="default">
      <header className="flex flex-col gap-2">
        <h1 className="text-[22px] font-semibold leading-[28px] text-[var(--tvl-ink)] md:text-[26px]">
          {t('com.rep.title', 'Moje leady / My leads')}
        </h1>
        <div className="flex items-center gap-3 text-[13px] leading-[18px] text-[var(--tvl-ink-2)]">
          <span className="tabular-nums">
            {t('com.rep.openCount', '{{count}} k oslovení / {{count}} to contact', { count: active.length })}
          </span>
          {atRiskCount > 0 ? (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-[600] tabular-nums"
              style={{ backgroundColor: 'var(--tvl-accent)', color: 'var(--tvl-on-accent)' }}
            >
              {t('com.rep.atRisk', '{{count}} v termínu / {{count}} at risk', { count: atRiskCount })}
            </span>
          ) : null}
        </div>
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-64" />
        </div>
      ) : isError ? (
        <EmptyState
          variant="error-loading"
          title={t('com.rep.error', 'Leady se nepodařilo načíst / Could not load your leads')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t('common.retry', 'Zkusit znovu / Retry')}
            </Button>
          }
        />
      ) : (
        <Tabs
          selectedKey={tab}
          onSelectionChange={(key) => setTab(key as RepTaskTab)}
        >
          <TabsList aria-label={t('com.rep.tabs', 'Filtr leadů / Lead filter')}>
            <TabsTrigger id="active">
              {t('com.rep.tab.active', 'Aktivní / Active')} ({active.length})
            </TabsTrigger>
            <TabsTrigger id="history">
              {t('com.rep.tab.history', 'Historie / History')} ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent id="active">
            <DataTable<RepTask>
              id="com-rep-active"
              data={active}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/leads/${row.id}`)}
              emptyTitle={t('com.rep.empty.active', 'Žádné leady k oslovení / No leads to contact')}
              emptyDescription={t(
                'com.rep.empty.active_desc',
                'Jakmile vám agent předá konverzaci, objeví se zde. / When the agent hands a conversation to you, it appears here.',
              )}
            />
          </TabsContent>

          <TabsContent id="history">
            <DataTable<RepTask>
              id="com-rep-history"
              data={history}
              columns={columns}
              getRowId={(row) => row.id}
              onRowClick={(row) => navigate(`/leads/${row.id}`)}
              emptyTitle={t('com.rep.empty.history', 'Zatím žádná historie / No history yet')}
            />
          </TabsContent>
        </Tabs>
      )}
    </PageContainer>
  );
}
